'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import { useParams } from 'next/navigation';
import type { Share } from '@frameio/graphql-client/generated';
import { ensureString } from '@frameio/utils/formatters';

type ShareId = Share['id'] | undefined;

export const IsolatedActiveShareIdContext = createContext<ShareId>(undefined);

export const useActiveShareId = () => useContext(IsolatedActiveShareIdContext);

type IsolatedActiveShareContextProviderProps = {
  children: React.ReactNode;
  shareId?: ShareId;
};

export const IsolatedActiveShareIdContextProvider = ({
  shareId,
  children,
}: IsolatedActiveShareContextProviderProps) => {
  return (
    <IsolatedActiveShareIdContext.Provider value={shareId}>
      {children}
    </IsolatedActiveShareIdContext.Provider>
  );
};

/**
 * Helper context to derive the active share id from params or optionally
 * fall back to the root share id.
 */
export const ActiveShareIdContext = (
  props: PropsWithChildren<{
    shareId?: string;
  }>
) => {
  const params = useParams();

  return (
    <IsolatedActiveShareIdContextProvider
      shareId={ensureString(params.shareId) || props.shareId}
    >
      {props.children}
    </IsolatedActiveShareIdContextProvider>
  );
};
